import React, { useEffect, useState, useRef, useContext } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import styles from "./GameFinishPage.module.css";
import { Camera } from "lucide-react";
import { LanguageContext } from "./LanguageContext";
import { FINISH, pageT } from "../utils/i18n";

export default function GameFinishPage() {
    const navigate = useNavigate();
    const { lang } = useContext(LanguageContext);
    const t = pageT(lang, FINISH);
    const [envelopes, setEnvelopes] = useState({ silver: false, gold: false, diamond: false });
    const [flyingCoins, setFlyingCoins] = useState([]);
    const [showGameOver, setShowGameOver] = useState(false); 
    const pageRef = useRef(null);


    useEffect(() => {
        if (envelopes.silver && envelopes.gold && envelopes.diamond) {
         
            const timer = setTimeout(() => {
                setShowGameOver(true);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [envelopes]);

    useEffect(() => {
        if (!window.html2canvas) {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    const handleEnvelopeClick = (type, e) => {
        if (envelopes[type]) return;
        setEnvelopes(prev => ({ ...prev, [type]: true }));

        const rect = e.currentTarget.getBoundingClientRect();
        const startX = rect.left + rect.width / 2;
        const startY = rect.top + rect.height / 2;
        const coinCount = type === 'diamond' ? 60 : type === 'gold' ? 45 : 30;

        const newCoins = Array.from({ length: coinCount }).map((_, i) => ({
            id: Date.now() + i + Math.random(),
            startX: startX,
            startY: startY,
            destX: window.innerWidth / 2 + (Math.random() - 0.5) * 300,
            destY: window.innerHeight - 50 + (Math.random() - 0.5) * 50,
            rotation: Math.random() * 720,
            delay: Math.random() * 0.5
        }));
        setFlyingCoins(prev => [...prev, ...newCoins]);
    };

    const handleScreenshot = async () => {
        if (!window.html2canvas) {
            alert(t.toolLoading);
            return;
        }

        try {
            const canvas = await window.html2canvas(pageRef.current, {
                useCORS: true,
                backgroundColor: null,
                scale: 2
            });

            canvas.toBlob(async (blob) => {
                if (!blob) return;
                const file = new File([blob], "chit-master-win.png", { type: "image/png" });

                if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                    try {
                        await navigator.share({
                            title: t.victory,
                            text: t.shareText,
                            files: [file]
                        });
                    } catch (err) { console.log("Share canceled"); }
                } else {
                    const link = document.createElement('a');
                    link.href = canvas.toDataURL("image/png");
                    link.download = 'chit-master-win.png';
                    link.click();
                }
            });
        } catch (err) { console.error(err); }
    };

    return (
        <div className={styles.container} ref={pageRef}>
         
            <div className={styles.bgShapes}>
                <div className={`${styles.shape} ${styles.shape1}`}></div>
                <div className={`${styles.shape} ${styles.shape2}`}></div>
                <div className={`${styles.shape} ${styles.shape3}`}></div>
            </div>

            {/* Dynamic Coins */}
            <div className={styles.coinLayer}>
                <AnimatePresence>
                    {flyingCoins.map(coin => (
                        <motion.div
                            key={coin.id}
                            className={styles.dynamicCoin}
                            initial={{ x: coin.startX, y: coin.startY, scale: 0.1, opacity: 0 }}
                            animate={{ x: coin.destX, y: coin.destY, scale: 1, opacity: 1, rotate: coin.rotation }}
                            transition={{ duration: 1, ease: "circOut", delay: coin.delay }}
                        // Coins stay on screen
                        />
                    ))}
                </AnimatePresence>
            </div>

            {/* GAME OVER RIBBON BANNER */}
            <div className={styles.bannerWrapper}>
                <div className={styles.ribbon}>
                    <h1 className={styles.bannerText}>{t.gameOver}</h1>
                </div>
            </div>

            <p className={styles.subtitle}>{t.clickEnvelopes}</p>

            <div className={styles.showcaseContainer}>
                {/* Silver */}
                <div className={styles.envelopeWrapper} onClick={(e) => handleEnvelopeClick('silver', e)}>
                    <div className={`${styles.silverEnvelope} ${envelopes.silver ? styles.open : ''}`}>
                        <div className={styles.innerContent}><div className={styles.coinPile}>💰</div></div>
                        <div className={styles.silverBase}></div><div className={styles.silverPocket}></div><div className={styles.silverFlap}></div>
                    </div>
                </div>

                {/* Gold */}
                <div className={styles.envelopeWrapper} onClick={(e) => handleEnvelopeClick('gold', e)}>
                    <div className={`${styles.goldEnvelope} ${envelopes.gold ? styles.open : ''}`}>
                        <div className={styles.innerContent}><div className={styles.coinPile} style={{ fontSize: '3.5rem' }}>💰💰</div></div>
                        <div className={styles.goldBase}></div><div className={styles.goldPocket}></div><div className={styles.goldFlap}></div>
                    </div>
                </div>

                {/* Diamond */}
                <div className={styles.envelopeWrapper} onClick={(e) => handleEnvelopeClick('diamond', e)}>
                    <div className={`${styles.diamondEnvelope} ${envelopes.diamond ? styles.open : ''}`}>
                        <div className={styles.innerContent}><div className={styles.coinPile} style={{ fontSize: '4rem' }}>💎💰</div></div>
                        <div className={styles.diamondBack}></div><div className={styles.diamondPocket}></div><div className={styles.diamondFlap}></div>
                    </div>
                </div>
            </div>

        
            <div className={styles.bottomSection}>
                <div className={styles.screenshotWrapper}>
                    <button className={styles.screenshotBtn} onClick={handleScreenshot}>
                        <Camera size={32} />
                    </button>
                    <div className={styles.tooltip}>
                        {t.screenshotTip}
                    </div>
                </div>
            </div>

            {/* FINAL GAME OVER OVERLAY */}
            {showGameOver && (
                <div className={styles.gameOverOverlay}>
                    {/* Confetti Canvas Placeholder */}
                    <canvas id="confetti-canvas" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}></canvas>

                    <div className={styles.card3DContainer}>
                        {/* Rotating Sunburst Rays */}
                        <div className={styles.rays}></div>

                        <div className={styles.gameOverCard}>
                            {/* Smaller Ribbon for Modal */}
                            <div className={styles.ribbon} style={{ transform: 'none', marginBottom: '20px', padding: '10px 40px', translateZ: '30px' }}>
                                <h2 className={styles.bannerText} style={{ fontSize: '2rem' }}>{t.gameOver}</h2>
                            </div>

                            <h2 className={styles.finalTitle}>{t.allCollected}</h2>
                            <p className={styles.finalText}>
                                {t.finalText}
                            </p>

                            <button
                                className={styles.accountBtn}
                                onClick={() => navigate("/account")}
                                style={{ border: '2px solid #FFD700', color: '#FFD700' }}
                            >
                                {t.goAccount}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}